import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { getStripePromise } from "~/helpers/stripe";

import { usePlanFeature } from "~/hooks/usePlanFeature";
import { Elements } from "@stripe/react-stripe-js";
import _debounce from "lodash/debounce";
import Modal from "~/components/ui/Modal";
import { SVG } from "~/components/ui/SVG";
import {
  SHOW_LIMITS_MODAL,
  triggerShowPlansModal,
  triggerShowResumeModal,
} from "~/helpers/customEvents";
import { Limits } from "~/types/billing";
import * as S from "./styles";
import { useTranslation } from "react-i18next";
import { getFeaturesTranslations } from "~/constants/features";
import FeaturesTestimonialImg from "~/assets/png/features-testimonial.png";
import { useMinimumPlanForLimit } from "~/hooks/useMinimumPlanForLimit";
import { usePlans } from "~/api/billing/queries";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { usePaymentMethod } from "~/hooks/usePaymentMethod";
import { useNextPlanForLimit } from "~/hooks/useNextPlanForLimit";
import { useShopifyPayment } from "~/pages/checkout/useShopifyPayment";
import handleErrors from "~/helpers/handleErrors";
import { useAccount } from "~/hooks/useAccount";
import { useSetupIntent } from "~/pages/checkout/useSetupIntent";
import { useStripeConfirmation } from "~/pages/checkout/useStripeConfirmation";
import { toast } from "../toast";
import { useUpdateCard } from "~/api/billing/queries";
import { formConfig } from "~/pages/checkout/form";
import { Form } from "~/components/ui/Form";
import { useForm } from "~/hooks/useForm";
import Button from "~/components/ui/Button";
import LoaderSVG from "~/assets/svg/loader.svg?react";
import { triggerGTMAddPaymentInfo } from "~/helpers/gtm";

interface Props {
  limitType: any;
  hide: () => void;
}

const LimitsModalInternal = ({ limitType, hide }: Props) => {
  const { minimumPlan, hasAbovePlans } = useMinimumPlanForLimit(limitType);
  const { t } = useTranslation();
  const { account, getAccount } = useAccount();
  const { isUserShopify } = usePaymentMethod();
  const { nextPlan } = useNextPlanForLimit(
    limitType,
    account?.active_subscription?.plan
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const payShopify = useShopifyPayment(nextPlan?.id || "");

  // Card update logic
  const { mutateAsync: updateCard } = useUpdateCard();
  const cardForm = useForm(formConfig);

  if (!minimumPlan) return null;

  const translations = getFeaturesTranslations(
    t,
    limitType,
    minimumPlan.name,
    hasAbovePlans
  );

  const handleShopifyPayment = async () => {
    const messages = {
      success: null,
      error: t("checkout.error"),
      loading: null,
    };
    const { response, errors } = await handleErrors(payShopify, messages);
    if (!errors) {
      localStorage.removeItem("onboarding_with_create_store");
    }

    if (response.confirmation_url)
      window.location.replace(response.confirmation_url);
  };

  const handleCardClear = () => {
    // cardForm.reset();
    setShowCardForm(false);
  };
  // Handle card update
  const handleCardUpdate = async () => {
    const values = cardForm.getValues();
    const toastMessages = {
      loading: t("checkout.update_card"),
      success: t("checkout.update_success"),
      error: t("checkout.update_failed"),
    };

    if (showCardForm || !account?.stripe_card_digits) {
      const { errors } = await handleErrors(
        () => updateCard({ ...values }),
        toastMessages
      );

      if (!errors) {
        setShowCardForm(false);
        getAccount();
        triggerGTMAddPaymentInfo();
      }
    } else {
      setShowCardForm(true);
    }
  };

  const getSetupIntent = useSetupIntent();
  const [clientSecret, setClientSecret] = useState();

  const getClientSecretDebounced = useCallback(
    _debounce(async (account) => {
      if (!clientSecret) {
        const response = await getSetupIntent(account);
        setClientSecret(response?.client_secret);
      }
    }, 200),
    [clientSecret]
  );

  useEffect(() => {
    if (!account) {
      return;
    }
    getClientSecretDebounced(account);
  }, [account]);

  const CheckoutStripe = () => {
    const upgradeWithStore =
      localStorage.getItem("onboarding_with_create_store") === "true";

    const { handlePayment: handleStripePayment } = useStripeConfirmation(
      nextPlan,
      upgradeWithStore,
      (value: any) => {
        setIsLoading(value);
      },
      () => {
        hide();
        toast.success("Congratulations! Your plan was successfully upgraded");
      }
    );

    if (isLoading) {
      return <LoaderSVG />;
    }
    return (
      <S.SwitchButton
        onClick={() => {
          if (!minimumPlan.id) {
            triggerShowPlansModal();
          } else {
            handleStripePayment();
          }
        }}>
        {t("features.switch_to_plan", { planName: nextPlan?.name })}
      </S.SwitchButton>
    );
  };

  const CheckoutStripeWrapper = () => {
    const appearance = {
      theme: "stripe",
    };
    const options = {
      clientSecret: clientSecret,
      appearance,
    };

    return (
      //@ts-ignore
      <Elements options={options} stripe={getStripePromise()}>
        <CheckoutStripe />
      </Elements>
    );
  };

  if (limitType === Limits.ADS_WRITER) {
    const texts = getFeaturesTranslations(
      t,
      limitType,
      nextPlan?.name ?? "",
      false
    );

    return (
      <Modal
        id="plan-limits"
        isShowing={true}
        hide={hide}
        padding="24px"
        minWidth="min(90%, 480px)">
        <S.IconContainer>
          <SVG icon={faLock} color="primary" size="xl" />
        </S.IconContainer>

        <S.Title>{texts.title}</S.Title>

        <S.Description>{texts.description}</S.Description>

        <S.TestimonialContainer>
          <S.TestimonialImage src={FeaturesTestimonialImg} />

          <S.TestimonialText>{texts.testimonial}</S.TestimonialText>
        </S.TestimonialContainer>

        {/* Updated Card Section with business logic */}
        {showCardForm || !account?.stripe_card_digits ? (
          <S.CardEditContainer>
            <Form {...cardForm} />
            <S.ButtonContainer>
              <Button
                onClick={handleCardUpdate}
                bgColor="primaryLight"
                color="primary"
                isDisabled={!cardForm.isDirty}
                alignSelf="flex-end"
                fontWeight={600}>
                {t("checkout.save")}
              </Button>
              <Button
                onClick={handleCardClear}
                bgColor="primary"
                color="white"
                isDisabled={!cardForm.isDirty}
                alignSelf="flex-end"
                fontWeight={600}>
                {t("dropshipping.cancel")}
              </Button>
            </S.ButtonContainer>
          </S.CardEditContainer>
        ) : (
          <S.SavedCardSection>
            <S.SavedCardTitle>
              {t("features.saved_card_title")}
            </S.SavedCardTitle>
            <S.SavedCardContainer>
              <S.CardDetails>
                <S.CardType>{t("features.credit_card")}</S.CardType>
                <S.CardNumber>{`**** **** **** ${account?.stripe_card_digits}`}</S.CardNumber>
              </S.CardDetails>
              <S.ChangeCardButton onClick={handleCardUpdate}>
                {t("features.change_card")}
              </S.ChangeCardButton>
            </S.SavedCardContainer>
          </S.SavedCardSection>
        )}

        {isUserShopify ? (
          <S.SwitchButton
            onClick={() => {
              hide();
              if (!minimumPlan.id) {
                triggerShowPlansModal();
              } else {
                handleShopifyPayment();
              }
            }}>
            {t("features.switch_to_plan", { planName: nextPlan?.name })}
          </S.SwitchButton>
        ) : (
          <CheckoutStripeWrapper />
        )}
        <S.DisclaimerText>
          {t("features.no_fee_description", { planName: nextPlan?.name })}
        </S.DisclaimerText>
      </Modal>
    );
  }

  return (
    <Modal
      id="plan-limits"
      isShowing={true}
      hide={hide}
      padding="24px"
      minWidth="min(90%, 480px)">
      <S.IconContainer>
        <SVG icon={faLock} color="primary" size="xl" />
      </S.IconContainer>

      <S.Title>{translations.title}</S.Title>

      <S.Description>{translations.description}</S.Description>

      <S.TestimonialContainer>
        <S.TestimonialImage src={FeaturesTestimonialImg} />

        <S.TestimonialText>{translations.testimonial}</S.TestimonialText>
      </S.TestimonialContainer>

      {/* Updated Card Section with business logic */}
      {showCardForm || !account?.stripe_card_digits ? (
        <S.CardEditContainer>
          <Form {...cardForm} />
          <S.ButtonContainer>
            <Button
              onClick={handleCardUpdate}
              bgColor="primaryLight"
              color="primary"
              isDisabled={!cardForm.isDirty}
              alignSelf="flex-end"
              fontWeight={600}>
              {t("checkout.save")}
            </Button>
            <Button
              onClick={handleCardClear}
              bgColor="primary"
              color="white"
              isDisabled={!cardForm.isDirty}
              alignSelf="flex-end"
              fontWeight={600}>
              {t("dropshipping.cancel")}
            </Button>
          </S.ButtonContainer>
        </S.CardEditContainer>
      ) : (
        <S.SavedCardSection>
          <S.SavedCardTitle>{t("features.saved_card_title")}</S.SavedCardTitle>
          <S.SavedCardContainer>
            <S.CardDetails>
              <S.CardType>{t("features.credit_card")}</S.CardType>
              <S.CardNumber>{`**** **** **** ${account?.stripe_card_digits}`}</S.CardNumber>
            </S.CardDetails>
            <S.ChangeCardButton onClick={handleCardUpdate}>
              {t("features.change_card")}
            </S.ChangeCardButton>
          </S.SavedCardContainer>
        </S.SavedCardSection>
      )}

      {isUserShopify ? (
        <S.SwitchButton
          onClick={() => {
            hide();
            if (!minimumPlan.id) {
              triggerShowPlansModal();
            } else {
              handleShopifyPayment();
            }
          }}>
          {t("features.switch_to_plan", { planName: minimumPlan.name })}
        </S.SwitchButton>
      ) : (
        <CheckoutStripeWrapper />
      )}

      <S.DisclaimerText>
        {t("features.no_fee_description", { planName: minimumPlan.name })}
      </S.DisclaimerText>
    </Modal>
  );
};

export const LimitsModal = () => {
  const [shownLimit, setShownLimit] = useState<Limits | undefined>(undefined);
  const { isFeaturePaused } = usePlanFeature();
  const { data: plans } = usePlans();

  useEffect(() => {
    const handleEvent = ((e: CustomEvent<{ limitType: Limits }>) => {
      if (isFeaturePaused()) {
        triggerShowResumeModal();
      } else {
        setShownLimit(e.detail.limitType);
      }
    }) as EventListener;

    window.addEventListener(SHOW_LIMITS_MODAL, handleEvent);

    return () => {
      window.removeEventListener(SHOW_LIMITS_MODAL, handleEvent);
    };
  }, [isFeaturePaused]);

  if (!shownLimit || !plans || DISABLE_PAYMENTS) return null;

  return ReactDOM.createPortal(
    <LimitsModalInternal
      limitType={shownLimit}
      hide={() => setShownLimit(undefined)}
    />,
    document.body
  );
};
